import { ExclamationCircleOutlined } from "@ant-design/icons";
import { AnimationVariants, BaseAnimation } from "@di-devs/base-components";
import { SYSTEM_MESSAGES, ThemeDefaults } from "@di-devs/constants";
import {
	useBetterFeatureClient,
	useIsMountedRef,
	useModal,
	useSSEEvent,
} from "@di-devs/utils-fe";
import { useQueryClient } from "@tanstack/react-query";
import { Card, Col, Divider, Modal, message, Row, Table } from "antd";
import { motion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { SSE } from "../../../../shared/constants/sse.constants";
import type { Member } from "../../../../shared/types/memberCore.types";
import { QUERY_KEYS } from "../../../shared/constants/queries.keys";
import type { FeatureClient } from "../../../shared/types";
import { getCoreColumns } from "../../config/MemberColumns/coreColumns";
import { useMemberBrowse, useMemberDelete, useMemberDetail } from "../../hooks";
import { DEFAULT_MEMBERS_FILTERS_STATE } from "../../utils/memberHelpers";
import { MemberBrowseActions } from "../MemberBrowseActions";
import { MemberBrowseFilters } from "../MemberBrowseFilters";

const MotionColComponent = motion(Col);

export interface MemberBrowseTableProps {
	// Core props
	publicSettings?: any;

	// Integration points for sub-features
	chatComponent?: React.ComponentType<{
		visible: boolean;
		member: Member;
		onClose: () => void;
	}>;
	remarksComponent?: React.ComponentType<{
		visible: boolean;
		member: Member;
		onClose: () => void;
	}>;
	visitComponent?: React.ComponentType<any>;
	detailModalComponent?: React.ComponentType<{
		visible: boolean;
		member: Member | null | undefined;
		memberAttributes?: any[];
		onClose: () => void;
		onEdit: () => void;
		onDelete: (member: Member) => void;
	}>;
	editModalComponent?: React.ComponentType<{
		visible: boolean;
		member: Member | null | undefined;
		memberAttributes?: any[];
		onClose: () => void;
	}>;

	// Member attributes integration
	memberAttributes?: any[]; // Provided by member-attribute sub-feature
	attributeFiltersComponent?: React.ComponentType<{
		memberAttributes: any[];
	}>;
	getAttributeColumns?: (memberAttribute: any) => any; // Column generator for attributes

	// Callbacks
	onExport?: (filters: any) => void;
}

export const MemberBrowseTable: React.FC<MemberBrowseTableProps> = ({
	publicSettings,
	chatComponent: ChatComponent,
	remarksComponent: RemarksComponent,
	visitComponent: VisitComponent,
	detailModalComponent: DetailModalComponent,
	editModalComponent: EditModalComponent,
	memberAttributes = [],
	attributeFiltersComponent: AttributeFiltersComponent,
	getAttributeColumns,
	onExport,
}) => {
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();

	const memberId = searchParams.get("memberId");

	const isMountedRef = useIsMountedRef();
	const [modal, contextHolder] = Modal.useModal();
	const membersRef = useRef<any>();

	// State
	const [members, setMembers] = useState<Member[]>([]);
	const [currentMemberId, setCurrentMemberId] = useState<number | null>(null);
	const [_currentMember, setCurrentMember] = useState<Member | null>(null);
	const [filters, setFilters] = useState<any>(DEFAULT_MEMBERS_FILTERS_STATE);

	// Pagination state
	const [paginationPerPage, setPaginationPerPage] = useState(20);
	const [paginationPage, setPaginationPage] = useState(1);
	const [paginationTotal, setPaginationTotal] = useState(0);
	const [paginationSort, setPaginationSort] = useState("desc");
	const [paginationSortKey, setPaginationSortKey] = useState("updatedAt");

	// Modal states
	const [isMemberRemarksModalVisible, toggleMemberRemarksModal] = useModal();
	const [isMemberDetailModalVisible, toggleMemberDetailModal] = useModal();
	const [isMemberEditDetailModalVisible, toggleMemberEditModal] = useModal();
	const [isChatModalVisible, toggleMemberChatModal] = useModal();

	// Use member browse hook
	const membersQuery = useMemberBrowse(
		{
			paginationPerPage,
			paginationPage,
			paginationSort: paginationSort as "asc" | "desc",
			paginationSortKey,
			filters,
		},
		reactFeatureClient,
	);

	// Handle successful member browse data
	useEffect(() => {
		if (membersQuery.data && isMountedRef.current) {
			// The response should be direct since it's properly typed
			const responseData = membersQuery.data.data;
			if (responseData?.rows && responseData.rows.length > 0) {
				setMembers(responseData.rows);
			} else {
				setMembers([]);
			}
			setPaginationPerPage(responseData?.pp || 20);
			setPaginationPage(responseData?.p || 1);
			setPaginationTotal(responseData?.count || 0);
			setPaginationSort(responseData?.sort || "desc");
			setPaginationSortKey(responseData?.sortKey || "updatedAt");
		}
	}, [membersQuery.data, isMountedRef]);

	const memberDetailQuery = useMemberDetail(
		currentMemberId,
		reactFeatureClient,
	);

	const memberDeleteMutation = useMemberDelete(reactFeatureClient);

	// Add success message for delete operations
	useEffect(() => {
		if (memberDeleteMutation.isSuccess) {
			message.success(SYSTEM_MESSAGES.SUCCESS.DELETE_COMPLETED);
		}
	}, [memberDeleteMutation.isSuccess]);

	// Effects
	useEffect(() => {
		setFilters({
			...filters,
			memberId,
		});
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBERS] });
	}, [memberId]);

	useEffect(() => {
		membersRef.current = members;
	}, [members]);

	// SSE handlers
	const memberHandler = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBERS] });
	}, []);

	useSSEEvent(SSE.MEMBER, memberHandler);

	// Column handlers
	const columnHandlers = {
		showChatModal: (member: Member) => {
			if (ChatComponent) {
				setCurrentMember(member);
				if (member.memberId) setCurrentMemberId(member.memberId);
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.MEMBER_DETAIL, member.memberId],
				});
				toggleMemberChatModal();
			} else {
				message.info("チャット機能は利用できません");
			}
		},
		showMemberRemarksModal: (memberId: number) => {
			setCurrentMemberId(memberId);
			toggleMemberRemarksModal();
		},
		showMemberDetailModal: (memberId: number) => {
			setCurrentMemberId(memberId);
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.MEMBER_DETAIL, memberId],
			});
			toggleMemberDetailModal();
		},
	};

	// Build columns: core + dynamic attributes
	const buildColumns = () => {
		const coreColumns = getCoreColumns(columnHandlers);

		// If attributes are provided and column generator exists, add attribute columns
		if (memberAttributes.length > 0 && getAttributeColumns) {
			const attributeColumns = memberAttributes
				.filter((attr) => attr.isAdminDisplayed)
				.map(getAttributeColumns)
				.filter(Boolean);

			// Insert attribute columns before the last few action columns
			const actionColumnCount = 2; // Remarks and Details columns
			const insertIndex = coreColumns.length - actionColumnCount;
			return [
				...coreColumns.slice(0, insertIndex),
				...attributeColumns,
				...coreColumns.slice(insertIndex),
			];
		}

		return coreColumns;
	};

	const columns = buildColumns();

	// Handle table changes
	const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
		if (pagination.pageSize !== paginationPerPage) {
			setPaginationPerPage(pagination.pageSize);
		}

		if (pagination.current !== paginationPage) {
			setPaginationPage(pagination.current);
		}

		if (sorter?.order) {
			if (sorter.order === "ascend") {
				if (sorter.order !== paginationSort) {
					setPaginationSort("asc");
				}
			} else if (sorter.order === "descend") {
				if (sorter.order !== paginationSort) {
					setPaginationSort("desc");
				}
			}
		}

		if (sorter?.field) {
			if (sorter.field !== paginationSortKey) {
				setPaginationSortKey(sorter.field);
			}
		}
	};

	// Handle member deletion
	const handleMemberDelete = (member: Member) => {
		const paramData = {
			memberId: String(member.memberId),
		};

		modal.confirm({
			title: "確認",
			icon: (
				<ExclamationCircleOutlined
					style={{ color: ThemeDefaults.CUSTOM_RED }}
				/>
			),
			content: (
				<p>
					<span
						style={{ color: ThemeDefaults.CUSTOM_RED }}
					>{`${member.displayName || "ー"}`}</span>
					様の情報を削除してもよろしいでしょうか？
				</p>
			),
			okText: "削除",
			okButtonProps: {
				size: "large",
				type: "primary",
				danger: true,
			},
			cancelText: "閉じる",
			cancelButtonProps: {
				size: "large",
			},
			centered: true,
			onOk() {
				memberDeleteMutation.mutate(paramData);
				toggleMemberDetailModal();
			},
		});
	};

	const showMemberEditDetailModal = () => {
		toggleMemberDetailModal();
		toggleMemberEditModal();
	};

	const hideMemberEditDetailModal = () => {
		toggleMemberEditModal();
		toggleMemberDetailModal();
	};

	// Get current member data
	const currentMemberData = memberDetailQuery.data?.data;

	// Transform members for table
	const dataSource = members.map((member: Member) => ({
		key: member.memberId,
		member: member,
		...member,
	}));

	return (
		<>
			<BaseAnimation>
				<Card bordered={false} className="shadow-none">
					<Row>
						<Col xs={24}>
							<MemberBrowseFilters
								filters={filters}
								setFilters={setFilters}
								memberAttributes={memberAttributes}
								attributeFiltersComponent={AttributeFiltersComponent}
							/>
						</Col>
						<Divider />

						<MotionColComponent
							xs={24}
							variants={AnimationVariants.StaggerContainer}
							initial="hidden"
							animate="show"
							exit="hidden"
						>
							<motion.div variants={AnimationVariants.StaggerItem}>
								{/* Actions Bar */}
								<div className="flex justify-between mb-2">
									{VisitComponent && (
										<VisitComponent publicSettings={publicSettings} />
									)}
									<MemberBrowseActions filters={filters} onExport={onExport} />
								</div>

								{/* Main Table */}
								<Table
									bordered
									size="small"
									loading={membersQuery.isPending}
									columns={columns}
									scroll={{ x: 640, y: 720 }}
									locale={{ emptyText: "お客様のデータがありません。" }}
									//@ts-expect-error
									onChange={handleTableChange}
									pagination={{
										responsive: true,
										showSizeChanger: true,
										onShowSizeChange: (_current, pageSize) =>
											setPaginationPerPage(pageSize),
										current: paginationPage,
										pageSize: paginationPerPage,
										total: paginationTotal,
										showTotal: (total, range) =>
											`全${total}件中${range[0]}～${range[1]}件目`,
										defaultCurrent: 1,
										defaultPageSize: 20,
										position: ["bottomCenter"],
									}}
									dataSource={dataSource}
								/>
							</motion.div>
						</MotionColComponent>
					</Row>
				</Card>
			</BaseAnimation>

			{/* Modals - use provided components or defaults */}
			{ChatComponent && (
				<ChatComponent
					member={currentMemberData!}
					visible={isChatModalVisible}
					onClose={toggleMemberChatModal}
				/>
			)}
			{RemarksComponent && (
				<RemarksComponent
					member={currentMemberData!}
					visible={isMemberRemarksModalVisible}
					onClose={toggleMemberRemarksModal}
				/>
			)}

			{DetailModalComponent && (
				<DetailModalComponent
					visible={isMemberDetailModalVisible}
					member={currentMemberData}
					memberAttributes={memberAttributes}
					onClose={toggleMemberDetailModal}
					onEdit={showMemberEditDetailModal}
					onDelete={handleMemberDelete}
				/>
			)}

			{EditModalComponent && (
				<EditModalComponent
					visible={isMemberEditDetailModalVisible}
					member={currentMemberData}
					memberAttributes={memberAttributes}
					onClose={hideMemberEditDetailModal}
				/>
			)}

			{contextHolder}
		</>
	);
};
